<template>
  <div class="h-full flex flex-col">
    <l-modal-header
      :title="t('generate-invoice.new-company')"
      :show-back="true"
      @back="emit('back')"
    />

    <l-modal-content>
      <l-form ref="formRef" :validation-schema="formSchema" @submit="handleSave">
        <div class="flex gap-4">
          <l-form-field class="w-full" v-slot="{ field }" name="taxId">
            <l-form-label>{{ t('generate-invoice.tax-id') }}</l-form-label>
            <l-input v-bind="field" @blur="handleTaxIdBlur" />
            <l-form-error />
          </l-form-field>
          <l-form-field class="w-full" v-slot="{ field }" name="name">
            <l-form-label>{{ t('generate-invoice.name') }}</l-form-label>
            <l-input v-bind="field" />
            <l-form-error />
          </l-form-field>
        </div>

        <l-form-field v-slot="{ field }" name="address">
          <l-form-label>{{ t('generate-invoice.address') }}</l-form-label>
          <l-input ref="addressInputRef" v-bind="field" />
          <l-form-error />
        </l-form-field>
      </l-form>
    </l-modal-content>

    <l-modal-footer>
      <l-button class="w-full" :loading="validating" @click="formRef?.requestSubmit()">
        {{ t('ctas.save') }}
      </l-button>
    </l-modal-footer>
  </div>
</template>

<script setup lang="ts">
import { Loader } from '@googlemaps/js-api-loader'
import { toTypedSchema } from '@vee-validate/zod'
import { v4 as uuid } from 'uuid'
import type { ComponentInstance } from 'vue'
import { onMounted, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { z } from 'zod'

import {
  LButton,
  LForm,
  LFormError,
  LFormField,
  LFormLabel,
  LInput,
  LModalContent,
  LModalFooter,
  LModalHeader
} from '@last/core-ui/paprika'

import { legalEntitiesApi } from '@/api/client'
import { useConfigStore } from '@/store/config'
import type { Company } from '@/types'

import { zodErrorMessages } from '../reservations/zod-error-messages'

z.setErrorMap(zodErrorMessages)

const props = defineProps<{ company?: Company }>()
const emit = defineEmits<{
  save: [Company]
  back: []
}>()

const { t } = useI18n()
const formRef = useTemplateRef('formRef')
const configStore = useConfigStore()

const addressInputRef = ref<ComponentInstance<typeof LInput>>()

const validating = ref(false)

const schema = z.object({
  taxId: z.string().trim().nonempty(),
  name: z.string().trim().nonempty(),
  address: z.string().trim().nonempty()
})

const formSchema = toTypedSchema(schema)

type FormValues = z.infer<typeof schema>

async function handleSave(values: FormValues) {
  validating.value = true
  try {
    const result = await legalEntitiesApi.validateLegalEntity({
      validateLegalEntityRequest: {
        taxId: values.taxId,
        companyName: values.name,
        countryCode: configStore.config.locationCountryCode,
        address: values.address
      }
    })

    if (!result.isValid) {
      formRef.value?.setFieldError('taxId', t('generate-invoice.invalid-tax-id'))
      return
    }
  } catch {
    // Error handled silently during validation
  } finally {
    validating.value = false
  }

  emit('save', { id: props.company?.id ?? uuid(), ...values })
}

async function handleTaxIdBlur() {
  const taxId = formRef.value?.values.taxId
  if (!taxId) return

  try {
    const result = await legalEntitiesApi.validateLegalEntity({
      validateLegalEntityRequest: {
        taxId,
        countryCode: configStore.config.locationCountryCode
      }
    })

    if (result.isValid && result.companyName) {
      formRef.value?.setFieldValue('name', result.companyName)
    }
  } catch {
    // Error handled silently during validation
  }
}

async function initAddressAutocomplete() {
  if (!addressInputRef.value?.inputRef) return
  const loader = new Loader({
    apiKey: import.meta.env.VITE_GOOGLE_KEY
  })
  const { Autocomplete } = await loader.importLibrary('places')
  const autocomplete = new Autocomplete(addressInputRef.value.inputRef, {
    componentRestrictions: {
      country: ['es', 'ad', 'de', 'mx']
    },
    fields: ['formatted_address', 'address_component']
  })
  autocomplete.addListener('place_changed', () => {
    const place = autocomplete.getPlace()
    if (!place?.address_components) return
    formRef.value?.setFieldValue('address', place.formatted_address ?? '')
  })
}

watch(addressInputRef, () => {
  if (addressInputRef.value) {
    initAddressAutocomplete()
  }
})

onMounted(() => {
  if (props.company) {
    formRef.value?.setValues({
      taxId: props.company.taxId,
      name: props.company.name,
      address: props.company.address
    })
  }
})
</script>
